import {
  Button,
  Columns,
  ColumnsItem,
  Container,
  Modal,
  RowItem,
  Rows,
  Title,
  TitleLevels,
} from '@bytel/trilogy-react-ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TelephoneFilters } from './filter-hook';
import './filter-modal.css';

export interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  pendingFilters: TelephoneFilters;
  appliedFilters: TelephoneFilters;
  availableMarques: string[];
  availableModeles: string[];
  onUpdatePendingFilters: (filters: Partial<TelephoneFilters>) => void;
  onResetFilters: () => void;
  onApplyFilters: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  pendingFilters,
  appliedFilters,
  availableMarques,
  availableModeles,
  onUpdatePendingFilters,
  onResetFilters,
  onApplyFilters,
}) => {
  const { t } = useTranslation('domain/home', {
    keyPrefix: 'consommationTelephone.filtre',
  });
  // Vérifier s'il y a des filtres appliqués
  const hasAppliedFilters =
    appliedFilters.marques.length > 0 ||
    appliedFilters.modeles.length > 0 ||
    appliedFilters.dateDebut !== '' ||
    appliedFilters.dateFin !== '';

  const handleMarqueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    onUpdatePendingFilters({ marques: selectedOptions });
  };

  const handleModelesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    onUpdatePendingFilters({ modeles: selectedOptions });
  };

  const handleApply = () => {
    onApplyFilters();
  };

  const handleReset = () => {
    onResetFilters();
  };

  return (
    <Modal active={isOpen} onClose={onClose} closeIcon data-cy="telephone-filter-modal">
      <Container className="is-marginless" fluid>
        <Rows>
          <RowItem>
            <Title level={TitleLevels.TWO} data-cy="filter-modal-title">
              {t('titre')}
            </Title>
          </RowItem>

          {/* Section Marques */}
          <RowItem data-cy="filter-marques-section">
            <div role="group" aria-labelledby="marques-heading">
              <h3 id="marques-heading" className="title is-level-3" style={{ marginBottom: '12px' }}>
                {t('marques')}
              </h3>
              <select
                id="filter-marques-select"
                name="marques"
                className="select is-multiple"
                multiple
                size={1}
                value={pendingFilters.marques}
                onChange={handleMarqueChange}
                data-cy="filter-marques-select"
                style={{ width: '100%' }}
                aria-label="Sélectionnez les marques"
              >
                {availableMarques.map((marque) => (
                  <option key={marque} value={marque}>
                    {marque}
                  </option>
                ))}
              </select>
            </div>
          </RowItem>

          {/* Section Modèles */}
          <RowItem data-cy="filter-modeles-section">
            <div role="group" aria-labelledby="modeles-heading">
              <h3 id="modeles-heading" className="title is-level-3" style={{ marginBottom: '12px' }}>
                {t('modeles')}
              </h3>
              <select
                id="filter-modeles-select"
                name="modeles"
                className="select is-multiple"
                multiple
                size={1}
                value={pendingFilters.modeles}
                onChange={handleModelesChange}
                data-cy="filter-modeles-select"
                aria-label="Sélectionnez les modèles"
              >
                {availableModeles.map((modele) => (
                  <option key={modele} value={modele}>
                    {modele}
                  </option>
                ))}
              </select>
            </div>
          </RowItem>

          {/* Section Dates */}
          <RowItem data-cy="filter-dates-section">
            <div role="group" aria-labelledby="periode-heading">
              <h3 id="periode-heading" className="title is-level-3" style={{ marginBottom: '12px' }}>
                {t('date')}
              </h3>
              <Columns>
                <ColumnsItem size={6}>
                  <input
                    id="date-debut"
                    name="dateDebut"
                    type="date"
                    className="input"
                    value={pendingFilters.dateDebut}
                    onChange={(e) => onUpdatePendingFilters({ dateDebut: e.target.value })}
                    data-cy="filter-date-debut"
                    aria-label={t('dateDebut')}
                  />
                </ColumnsItem>
                <ColumnsItem size={6}>
                  <input
                    id="date-fin"
                    name="dateFin"
                    type="date"
                    className="input"
                    value={pendingFilters.dateFin}
                    onChange={(e) => onUpdatePendingFilters({ dateFin: e.target.value })}
                    data-cy="filter-date-fin"
                    aria-label={t('dateFin')}
                  />
                </ColumnsItem>
              </Columns>
            </div>
          </RowItem>

          {/* Boutons d'action */}
          <RowItem>
            <Columns>
              <ColumnsItem size={6}>
                <Button
                  onClick={handleReset}
                  fullwidth
                  data-cy="filter-reset-button"
                  variant="SECONDARY"
                  disabled={!hasAppliedFilters}
                >
                  {t('reset')}
                </Button>
              </ColumnsItem>
              <ColumnsItem size={6}>
                <Button onClick={handleApply} fullwidth data-cy="filter-apply-button" variant="PRIMARY">
                  {t('apply')}
                </Button>
              </ColumnsItem>
            </Columns>
          </RowItem>
        </Rows>
      </Container>
    </Modal>
  );
};

export default FilterModal;
